import { useMemo, useRef, useState } from "react";
import { useBranch } from "@/context/BranchContext";
import { useOrders } from "@/context/OrdersContext";
import {
  ORDER_STATUS,
  PAYMENT_METHODS,
  PICKUP_METHODS,
  SOURCE_LABELS,
  DATE_FILTERS,
  vatBreakdown,
  hasAppFee,
} from "@/pages/branch/orders/constants";
import type { OrderSource, OrderStatus, PaymentMethod, PickupMethod } from "@/mocks/branch/orders";
import Select from "@/components/base/Select";
import StatCard from "@/pages/branch/dashboard/components/StatCard";
import AccessDenied from "@/pages/branch/access-denied/page";
import BarList, { type BarItem } from "./components/BarList";
import DonutChart, { type DonutSegment } from "./components/DonutChart";
import SalesTrendChart, { type TrendPoint, type TrendMetric, type SalesTrendChartHandle } from "./components/SalesTrendChart";

const STATUS_COLORS: Record<OrderStatus, string> = {
  new: "#2E9E46",
  preparing: "#B7791F",
  almost_ready: "#E8590C",
  ready: "#0D9488",
  out_for_delivery: "#B45309",
  picked_up: "#6B7280",
  not_picked: "#DC2626",
};

const PAYMENT_COLORS: Record<PaymentMethod, string> = {
  card: "#2E9E46",
  apple_pay: "#1F2937",
  wallet: "#C98A00",
  cash: "#9CA3AF",
  pos_terminal: "#0D9488",
};

const PICKUP_COLORS: Record<PickupMethod, string> = {
  dine_in: "#2E9E46",
  takeaway: "#C98A00",
  car: "#E8590C",
  delivery: "#0D9488",
};

const SOURCE_COLORS: Record<OrderSource, string> = {
  app: "#2E9E46",
  pos: "#0D9488",
};

const ALL_STATUSES: OrderStatus[] = ["new", "preparing", "almost_ready", "ready", "picked_up", "not_picked"];

const DATE_OPTIONS = DATE_FILTERS.map((d) => ({ value: d.key, label: d.label }));
const SOURCE_OPTIONS = [
  { value: "all", label: "الكل" },
  ...(Object.keys(SOURCE_LABELS) as OrderSource[]).map((s) => ({
    value: s,
    label: SOURCE_LABELS[s].label,
    icon: SOURCE_LABELS[s].icon,
  })),
];
const PAYMENT_OPTIONS = [
  { value: "all", label: "الكل" },
  ...(Object.keys(PAYMENT_METHODS) as PaymentMethod[]).map((m) => ({
    value: m,
    label: PAYMENT_METHODS[m].label,
    icon: PAYMENT_METHODS[m].icon,
  })),
];
const PICKUP_OPTIONS = [
  { value: "all", label: "الكل" },
  ...(Object.keys(PICKUP_METHODS) as PickupMethod[]).map((m) => ({
    value: m,
    label: PICKUP_METHODS[m].label,
    icon: PICKUP_METHODS[m].icon,
  })),
];

export default function ReportsPage() {
  const { employee, can } = useBranch();
  const { orders } = useOrders();

  const [dateFilter, setDateFilter] = useState("today");
  const [sourceFilter, setSourceFilter] = useState<OrderSource | "all">("all");
  const [paymentFilter, setPaymentFilter] = useState<PaymentMethod | "all">("all");
  const [pickupFilter, setPickupFilter] = useState<PickupMethod | "all">("all");
  const [trendMetric, setTrendMetric] = useState<TrendMetric>("both");
  const trendChartRef = useRef<SalesTrendChartHandle>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);

  const hasAccess = can("reports");

  const filtered = useMemo(
    () =>
      orders.filter((o) => {
        if (sourceFilter !== "all" && o.source !== sourceFilter) return false;
        if (paymentFilter !== "all" && o.paymentMethod !== paymentFilter) return false;
        if (pickupFilter !== "all" && o.pickupMethod !== pickupFilter) return false;
        return true;
      }),
    [orders, sourceFilter, paymentFilter, pickupFilter],
  );

  const stats = useMemo(() => {
    const revenue = filtered.reduce((sum, o) => sum + o.total, 0);
    const count = filtered.length;
    const avgOrderValue = count ? revenue / count : 0;
    const { vat } = vatBreakdown(revenue);
    const rated = filtered.filter((o) => typeof o.rating === "number");
    const avgRating = rated.length
      ? rated.reduce((sum, o) => sum + (o.rating ?? 0), 0) / rated.length
      : 0;

    const statusItems: BarItem[] = ALL_STATUSES.map((s) => ({
      label: ORDER_STATUS[s].label,
      value: filtered.filter((o) => o.status === s).length,
      color: STATUS_COLORS[s],
    }));

    const sourceSegments: DonutSegment[] = (Object.keys(SOURCE_LABELS) as OrderSource[]).map((s) => ({
      label: SOURCE_LABELS[s].short,
      value: filtered.filter((o) => o.source === s).length,
      color: SOURCE_COLORS[s],
    }));

    const paymentSegments: DonutSegment[] = (Object.keys(PAYMENT_METHODS) as PaymentMethod[]).map((m) => ({
      label: PAYMENT_METHODS[m].label,
      value: filtered.filter((o) => o.paymentMethod === m).length,
      color: PAYMENT_COLORS[m],
    }));

    const pickupItems: BarItem[] = (Object.keys(PICKUP_METHODS) as PickupMethod[]).map((m) => ({
      label: PICKUP_METHODS[m].label,
      value: filtered.filter((o) => o.pickupMethod === m).length,
      color: PICKUP_COLORS[m],
    }));

    const sellerMap = new Map<string, number>();
    for (const o of filtered) {
      for (const item of o.items) {
        sellerMap.set(item.name, (sellerMap.get(item.name) || 0) + item.quantity);
      }
    }
    const bestSellers = Array.from(sellerMap.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
    const maxSeller = Math.max(1, ...bestSellers.map((b) => b.value));

    const notPicked = filtered.filter((o) => o.status === "not_picked").length;
    const appOrders = filtered.filter((o) => o.source === "app").length;
    const posOrders = filtered.filter((o) => o.source === "pos").length;
    // إجمالي رسوم التطبيق — تُحتسب على طلبات التطبيق غير المحلية فقط (دون الطلبات المحلية أو POS)
    const appFeeTotal = filtered
      .filter(hasAppFee)
      .reduce((sum, o) => sum + (o.appFee || 0), 0);

    const ratings = filtered
      .filter((o) => typeof o.rating === "number")
      .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));

    return {
      revenue,
      vat,
      count,
      avgOrderValue,
      avgRating,
      statusItems,
      sourceSegments,
      paymentSegments,
      pickupItems,
      bestSellers,
      maxSeller,
      notPicked,
      appOrders,
      posOrders,
      appFeeTotal,
      ratings,
    };
  }, [filtered]);

  const salesTrend = useMemo(() => {
    const byHourRevenue = new Map<number, number>();
    const byHourCount = new Map<number, number>();
    for (const o of filtered) {
      const hour = Number.parseInt(o.createdAt.split(":")[0], 10);
      if (Number.isNaN(hour)) continue;
      byHourRevenue.set(hour, (byHourRevenue.get(hour) || 0) + o.total);
      byHourCount.set(hour, (byHourCount.get(hour) || 0) + 1);
    }
    if (byHourRevenue.size === 0) {
      return {
        points: [] as TrendPoint[],
        peakRevenueLabel: "",
        totalRevenue: 0,
        peakCountLabel: "",
        totalCount: 0,
      };
    }
    const hours = Array.from(byHourRevenue.keys());
    const min = Math.min(...hours);
    const max = Math.max(...hours);
    const points: TrendPoint[] = [];
    let totalRevenue = 0;
    let peakRevenueLabel = "";
    let peakRevenueValue = 0;
    let totalCount = 0;
    let peakCountLabel = "";
    let peakCountValue = 0;
    for (let h = min; h <= max; h += 1) {
      const revenue = byHourRevenue.get(h) || 0;
      const count = byHourCount.get(h) || 0;
      const label = `${String(h).padStart(2, "0")}:00`;
      points.push({ label, revenue, count });
      totalRevenue += revenue;
      if (revenue > peakRevenueValue) {
        peakRevenueValue = revenue;
        peakRevenueLabel = label;
      }
      totalCount += count;
      if (count > peakCountValue) {
        peakCountValue = count;
        peakCountLabel = label;
      }
    }
    return {
      points,
      peakRevenueLabel,
      totalRevenue,
      peakCountLabel,
      totalCount,
    };
  }, [filtered]);

  const showTrendRevenue = trendMetric === "revenue" || trendMetric === "both";
  const showTrendCount = trendMetric === "count" || trendMetric === "both";

  const handleExportPng = async () => {
    if (isExporting) return;
    setIsExporting(true);
    setExportDone(false);
    try {
      const now = new Date();
      const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const dateStr = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()}`;
      const periodLabel = DATE_OPTIONS.find((d) => d.value === dateFilter)?.label ?? "";
      const metricLabel =
        trendMetric === "revenue"
          ? "مبلغ المبيعات"
          : trendMetric === "count"
            ? "عدد الطلبات"
            : "المبلغ وعدد الطلبات";
      await trendChartRef.current?.exportPng({
        filename: `اتجاه-المبيعات-${stamp}.png`,
        title: employee?.branchName || employee?.storeName || "تقرير الفرع",
        subtitle: `${periodLabel} · ${metricLabel} · ${dateStr}`,
      });
      setExportDone(true);
      window.setTimeout(() => setExportDone(false), 2500);
    } catch {
      setExportDone(false);
    } finally {
      setIsExporting(false);
    }
  };

  if (!hasAccess) {
    return <AccessDenied />;
  }

  return (
    <div className="max-w-[1400px] mx-auto space-y-5 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-fade-slide-up">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900">تقارير الفرع</h2>
          <p className="mt-1 text-sm text-foreground-500">
            مؤشرات الأداء وتوزيع الطلبات — طلبات التطبيق وطلبات POS منفصلة (بدون رسوم التطبيق على POS أو على الطلبات المحلية)
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="relative z-30 bg-background-50 rounded-card border border-background-200 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 animate-fade-slide-up">
        <label className="block min-w-0">
          <span className="block mb-1.5 text-xs font-semibold text-foreground-600">الفترة الزمنية</span>
          <Select
            value={dateFilter}
            options={DATE_OPTIONS}
            ariaLabel="الفترة الزمنية"
            onChange={setDateFilter}
          />
        </label>
        <label className="block min-w-0">
          <span className="block mb-1.5 text-xs font-semibold text-foreground-600">مصدر الطلب</span>
          <Select
            value={sourceFilter}
            options={SOURCE_OPTIONS}
            ariaLabel="مصدر الطلب"
            onChange={(v) => setSourceFilter(v as OrderSource | "all")}
          />
        </label>
        <label className="block min-w-0">
          <span className="block mb-1.5 text-xs font-semibold text-foreground-600">وسيلة الدفع</span>
          <Select
            value={paymentFilter}
            options={PAYMENT_OPTIONS}
            ariaLabel="وسيلة الدفع"
            onChange={(v) => setPaymentFilter(v as PaymentMethod | "all")}
          />
        </label>
        <label className="block min-w-0">
          <span className="block mb-1.5 text-xs font-semibold text-foreground-600">طريقة الاستلام</span>
          <Select
            value={pickupFilter}
            options={PICKUP_OPTIONS}
            ariaLabel="طريقة الاستلام"
            onChange={(v) => setPickupFilter(v as PickupMethod | "all")}
          />
        </label>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
        <StatCard label="إجمالي المبيعات" value={stats.revenue.toFixed(2)} icon="ri-money-dollar-circle-line" hint={`ر.س · ضريبة ${stats.vat.toFixed(2)}`} accent="primary" />
        <StatCard label="عدد الطلبات" value={String(stats.count)} icon="ri-file-list-3-line" hint={`${stats.appOrders} تطبيق · ${stats.posOrders} POS`} accent="accent" />
        <StatCard label="متوسط قيمة الطلب" value={stats.avgOrderValue.toFixed(2)} icon="ri-funds-line" hint="ر.س" accent="secondary" />
        <StatCard label="متوسط تقييم الفرع" value={stats.avgRating.toFixed(1)} icon="ri-star-line" hint="من أصل 5" accent="neutral" />
      </div>

      {/* App fee total — computed from app orders only */}
      <div className="flex items-center gap-3 px-4 py-3.5 rounded-card border border-background-200 bg-background-100 animate-fade-slide-up">
        <div
          className={`w-10 h-10 rounded-btn flex items-center justify-center shrink-0 ${
            sourceFilter === "pos"
              ? "bg-secondary-100 text-secondary-700"
              : "bg-primary-100 text-primary-700"
          }`}
        >
          <i className={sourceFilter === "pos" ? "ri-forbid-2-line text-xl" : "ri-percent-line text-xl"}></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-bold text-foreground-900">
              إجمالي رسوم التطبيق: {stats.appFeeTotal.toFixed(2)} ر.س
            </p>
            {sourceFilter === "pos" ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 text-secondary-900 px-2 py-0.5 text-[11px] font-bold whitespace-nowrap">
                <i className="ri-information-line"></i>
                لا تنطبق على طلبات POS
              </span>
            ) : null}
          </div>
          <p className="text-xs text-foreground-500 mt-0.5">
            {sourceFilter === "pos"
              ? "الفلتر الحالي معروض على طلبات POS داخل المطعم — لا تُحتسب عليها أي رسوم تطبيق، والقيمة 0.00 عن قصد."
              : `محتسبة على طلبات التطبيق غير المحلية (${stats.appOrders} طلب تطبيق) — ولا تُحتسب على الطلبات المحلية أو على طلبات POS.`}
          </p>
        </div>
      </div>

      {/* Not picked highlight */}
      <div className="flex items-center gap-3 px-4 py-3.5 rounded-card border border-red-200 bg-red-50">
        <div className="w-10 h-10 rounded-btn bg-red-100 text-red-600 flex items-center justify-center shrink-0">
          <i className="ri-error-warning-line text-xl"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-red-700">حالات «لم يُستلم»: {stats.notPicked}</p>
          <p className="text-xs text-red-600 mt-0.5">
            طلبات انتهت مهلتها دون استلام — لا تُصدر لها فاتورة وتبقى مديونية المنصة على المحل.
          </p>
        </div>
      </div>

      <div className="bg-background-50 rounded-card border border-background-200 p-4 md:p-5 animate-fade-slide-up">
        <div className="flex flex-col gap-3 mb-4 md:mb-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 className="text-sm md:text-base font-bold text-foreground-900 flex items-center gap-2">
              <i className="ri-line-chart-line text-primary-700"></i>
              اتجاه المبيعات على مدار اليوم
            </h3>
            <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
              <div className="flex flex-wrap items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
              <button
                type="button"
                onClick={() => setTrendMetric("revenue")}
                aria-pressed={trendMetric === "revenue"}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 sm:px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  trendMetric === "revenue"
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                <i className="ri-money-dollar-circle-line"></i>
                مبلغ المبيعات
              </button>
              <button
                type="button"
                onClick={() => setTrendMetric("count")}
                aria-pressed={trendMetric === "count"}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 sm:px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  trendMetric === "count"
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                <i className="ri-file-list-3-line"></i>
                عدد الطلبات
              </button>
              <button
                type="button"
                onClick={() => setTrendMetric("both")}
                aria-pressed={trendMetric === "both"}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 sm:px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  trendMetric === "both"
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                <i className="ri-bar-chart-grouped-line"></i>
                كلاهما
              </button>
              </div>
              <button
                type="button"
                onClick={handleExportPng}
                disabled={isExporting || salesTrend.points.length === 0}
                className="inline-flex items-center gap-1.5 rounded-full border border-background-300 bg-background-50 px-2.5 sm:px-3 py-1.5 text-xs font-semibold text-foreground-700 whitespace-nowrap transition-colors cursor-pointer hover:bg-background-100 hover:text-foreground-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i
                  className={
                    isExporting
                      ? "ri-loader-4-line animate-spin"
                      : exportDone
                        ? "ri-check-line text-primary-600"
                        : "ri-download-2-line"
                  }
                ></i>
                {isExporting ? "جارٍ التصدير..." : exportDone ? "تم التصدير" : "تصدير PNG"}
              </button>
            </div>
          </div>
          {salesTrend.points.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {showTrendRevenue && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 text-accent-900 px-2.5 py-1 text-xs font-semibold whitespace-nowrap">
                  <i className="ri-award-line"></i>
                  ذروة المبيعات {salesTrend.peakRevenueLabel}
                </span>
              )}
              {showTrendCount && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 text-accent-900 px-2.5 py-1 text-xs font-semibold whitespace-nowrap">
                  <i className="ri-award-line"></i>
                  ذروة الطلبات {salesTrend.peakCountLabel}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 text-primary-800 px-2.5 py-1 text-xs font-semibold whitespace-nowrap">
                <i className="ri-shopping-bag-3-line"></i>
                الإجمالي {showTrendRevenue ? `${salesTrend.totalRevenue.toFixed(2)} ر.س` : ""}
                {trendMetric === "both" ? " · " : ""}
                {showTrendCount ? `${salesTrend.totalCount} طلب` : ""}
              </span>
            </div>
          )}
        </div>
        <SalesTrendChart ref={trendChartRef} points={salesTrend.points} metric={trendMetric} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6">
        <div className="bg-background-50 rounded-card border border-background-200 p-4 md:p-5 animate-fade-slide-up">
          <h3 className="text-sm md:text-base font-bold text-foreground-900 flex items-center gap-2 mb-4 md:mb-5">
            <i className="ri-bar-chart-2-line text-primary-700"></i>
            الطلبات حسب الحالة
          </h3>
          <BarList items={stats.statusItems} />
        </div>

        <div className="bg-background-50 rounded-card border border-background-200 p-4 md:p-5 animate-fade-slide-up">
          <h3 className="text-sm md:text-base font-bold text-foreground-900 flex items-center gap-2 mb-4 md:mb-5">
            <i className="ri-store-3-line text-accent-700"></i>
            الطلبات حسب المصدر
          </h3>
          <DonutChart segments={stats.sourceSegments} centerLabel="طلب" centerValue={String(stats.count)} />
        </div>

        <div className="bg-background-50 rounded-card border border-background-200 p-4 md:p-5 animate-fade-slide-up">
          <h3 className="text-sm md:text-base font-bold text-foreground-900 flex items-center gap-2 mb-4 md:mb-5">
            <i className="ri-bank-card-line text-secondary-700"></i>
            الطلبات حسب وسيلة الدفع
          </h3>
          <DonutChart segments={stats.paymentSegments} centerLabel="طلب" centerValue={String(stats.count)} />
        </div>

        <div className="bg-background-50 rounded-card border border-background-200 p-4 md:p-5 animate-fade-slide-up">
          <h3 className="text-sm md:text-base font-bold text-foreground-900 flex items-center gap-2 mb-4 md:mb-5">
            <i className="ri-car-line text-accent-700"></i>
            الطلبات حسب طريقة الاستلام
          </h3>
          <BarList items={stats.pickupItems} />
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6">
        {/* Best sellers */}
        <div className="bg-background-50 rounded-card border border-background-200 p-4 md:p-5 animate-fade-slide-up">
          <h3 className="text-base font-bold text-foreground-900 flex items-center gap-2 mb-5">
            <i className="ri-fire-line text-primary-700"></i>
            المنتجات الأكثر مبيعًا
          </h3>
          {stats.bestSellers.length === 0 ? (
            <p className="text-sm text-foreground-500 text-center py-10">لا توجد بيانات مبيعات في هذه الفترة</p>
          ) : (
            <div className="space-y-3.5">
              {stats.bestSellers.map((item, idx) => {
                const width = Math.round((item.value / stats.maxSeller) * 100);
                return (
                  <div key={item.label}>
                    <div className="flex items-center justify-between gap-2 text-sm mb-1.5">
                      <span className="text-foreground-700 flex items-center gap-2 min-w-0">
                        <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-[11px] font-bold flex items-center justify-center shrink-0">
                          {idx + 1}
                        </span>
                        <span className="truncate">{item.label}</span>
                      </span>
                      <span className="text-foreground-900 font-semibold whitespace-nowrap shrink-0">
                        {item.value} طلب
                      </span>
                    </div>
                    <div className="h-2.5 rounded-full bg-background-200 overflow-hidden">
                      <div className="h-full rounded-full bg-primary-500 transition-all duration-500" style={{ width: `${width}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Customer ratings */}
        <div className="bg-background-50 rounded-card border border-background-200 p-4 md:p-5 animate-fade-slide-up">
          <h3 className="text-base font-bold text-foreground-900 flex items-center gap-2 mb-5">
            <i className="ri-star-line text-accent-700"></i>
            تقييمات العملاء للمحل
          </h3>
          {stats.ratings.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-star-line text-4xl text-foreground-300"></i>
              <p className="mt-3 text-sm text-foreground-500">لا توجد تقييمات في هذه الفترة</p>
            </div>
          ) : (
            <div className="divide-y divide-background-200">
              {stats.ratings.map((order) => (
                <div key={order.id} className="py-3 flex items-center gap-3">
                  <span className="text-sm font-bold text-foreground-900 shrink-0" dir="ltr">#{order.number}</span>
                  <span className="flex-1 text-sm text-foreground-600 truncate">{order.customerName}</span>
                  <span className="flex items-center gap-0.5 shrink-0">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`text-base ${i < (order.rating ?? 0) ? "ri-star-fill text-accent-500" : "ri-star-line text-foreground-300"}`}></i>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}